// Paket P2 — MembershipPromoHost (60_PROMOTION.md §2–§4).
//
// Kopfloser Host nach `CrossOnboardingHost`-Muster:
//   • zeigt die offene Server-Entscheidung des PromoProviders als Dialog
//     (`modal`) oder reicht sie als Banner-Ereignis an die Chat-Seiten
//     (`banner`, PROMO_BANNER_EVENT);
//   • zeigt den Intro-Dialog genau einmal, wenn `intro_due=true`;
//   • schweigt ueber Startup-Gates, auf ausgeschlossenen Routen und in den
//     ersten 90 s nach App-Start (Ausnahme Intro) — die Entscheidung bleibt
//     so lange liegen und wird danach gezeigt;
//   • haelt die Sitzungs-Caps (max. 1 Dialog, max. 2 Banner; Vorschau ohne).
//
// Der Provider haelt den State; hier lebt die Anzeige.

import { BillingIssueNotice } from '../pages/membership/BillingIssueNotice';
import { PurchaseStatusNotice } from '../pages/membership/PurchaseStatusNotice';
import { useAuth } from '../auth/AuthProvider';
import { relationMembership } from '../pages/membership/planRelation';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PromoDialog } from '../components/membership';
import { useMembership } from '../hooks/useMembership';
import { useT } from '../i18n';
import { usePromo, PROMO_PREVIEW_STORAGE_KEY } from '../promo/PromoProvider';
import { reportFunnelEvent } from '../api/promo';
import {
  canShowBanner, canShowDialog, bumpBanner, bumpDialog,
  isExcludedRoute,
  isIntroLockedThisSession,
  PROMO_START_LOCK_MS,
  type SessionCaps,
} from '../promo/promoClientRules';
import { useStartupGate } from './StartupGateCoordinator';

function readSessionStorage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.sessionStorage; }
  catch { return null; }
}

export interface BannerRequest {
  id: string;
  variant: string;
  surface: string | null;
  target_tier: string | null;
  /** Platzhalter {name} (Charakter-Chat). */
  characterName?: string;
}

interface PromoModal {
  id: string;
  variant: string;
  target_tier: string | null;
  isIntro: boolean;
  params: Record<string, string>;
}

export const PROMO_BANNER_EVENT = 'lorath:promo-banner';

const MEMBER_SEGMENTS = new Set(['plus', 'ultra', 'plus_trial']);

export function MembershipPromoHost() {
  const { t } = useT();
  const promo = usePromo();
  const gate = useStartupGate();
  const location = useLocation();
  const membership = useMembership();
  const auth = useAuth();
  const notices = <><PurchaseStatusNotice /><BillingIssueNotice contract={relationMembership(membership.snapshot)}
    accountKey={auth?.status === 'authed' && auth.user ? String(auth.user.id) : undefined} banner /></>;

  const [modal, setModal] = useState<PromoModal | null>(null);
  const capsRef = useRef<SessionCaps>({ dialogShown: 0, bannerShown: 0 });

  // 90-s-Startsperre als Zustand, damit eine wartende Entscheidung nach
  // Ablauf tatsaechlich gezeigt wird (ein reiner Zeitvergleich beim Render
  // wuerde nie erneut ausgewertet).
  const [lockOver, setLockOver] = useState<boolean>(() => (
    promo.sessionStartedAt > 0 && Date.now() - promo.sessionStartedAt >= PROMO_START_LOCK_MS
  ));
  useEffect(() => {
    if (lockOver) return undefined;
    const remaining = Math.max(0, PROMO_START_LOCK_MS - (Date.now() - promo.sessionStartedAt));
    const timer = window.setTimeout(() => setLockOver(true), remaining);
    return () => window.clearTimeout(timer);
  }, [lockOver, promo.sessionStartedAt]);

  const isPreview = promo.isPreview;
  const routeExcluded = isExcludedRoute(location.pathname);
  const segment = promo.snapshot?.segment ?? 'free_new';
  const introVariant = MEMBER_SEGMENTS.has(segment) ? 'intro.member' : 'intro.free';

  // Intro einmalig (nicht in der Startsperre gefangen — §2 Ausnahme), aber
  // nie ueber einem Gate, nie auf ausgeschlossenen Routen und nie, wenn der
  // Bestandsschutz-Dialog diese Sitzung die Intro-Sperre gesetzt hat.
  useEffect(() => {
    if (isPreview) return;
    if (gate !== 'none' || routeExcluded) return;
    if (!promo.snapshot?.intro_due) return;
    if (isIntroLockedThisSession(readSessionStorage())) return;
    setModal((current) => current ?? {
      id: 'promo-intro',
      variant: introVariant,
      target_tier: promo.snapshot?.target_tier ?? null,
      isIntro: true,
      params: {},
    });
  }, [gate, introVariant, isPreview, promo.snapshot, routeExcluded]);

  // Admin-Vorschau: "Intro-Dialog zeigen" (Einstellungen).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onShowIntro = () => {
      if (gate !== 'none') return;
      setModal({
        id: 'promo-intro-preview',
        variant: introVariant,
        target_tier: promo.snapshot?.target_tier ?? null,
        isIntro: true,
        params: {},
      });
    };
    window.addEventListener('lorath:promo-show-intro', onShowIntro as EventListener);
    return () => window.removeEventListener('lorath:promo-show-intro', onShowIntro as EventListener);
  }, [gate, introVariant, promo.snapshot?.target_tier]);

  // Offene Server-Entscheidung verarbeiten. Bleibt liegen, solange ein Gate,
  // die Route oder die Startsperre die Anzeige verbietet.
  const decision = promo.isPreview || promo.snapshot?.enabled !== false ? promo.decision : null;
  useEffect(() => {
    if (!decision) return;
    if (gate !== 'none' || routeExcluded) return;
    if (!lockOver && !isPreview) return;
    if (decision.kind === 'modal') {
      if (modal) return; // ein Dialog steht schon — warten
      if (!isPreview && !canShowDialog(capsRef.current)) {
        promo.consumeDecision(decision.id);
        return;
      }
      capsRef.current = bumpDialog(capsRef.current);
      // S6 Funnel: Ein Modal ist hier tatsaechlich sichtbar (Host rendert
      // den PromoDialog selbst). promo_view wird vom Host gemeldet, sobald
      // der Dialog rendert — Banner werden NICHT hier gemeldet, weil sie
      // erst in der Chat-Seite sichtbar werden.
      setModal({
        id: decision.id,
        variant: decision.variant || 'start.free',
        target_tier: decision.target_tier,
        isIntro: false,
        params: decision.params,
      });
      promo.consumeDecision(decision.id);
      return;
    }
    if (decision.kind === 'banner') {
      if (!isPreview && !canShowBanner(capsRef.current)) {
        promo.consumeDecision(decision.id);
        return;
      }
      capsRef.current = bumpBanner(capsRef.current);
      // S6 Funnel (P2-4): Banner wird hier NUR an die Chats gespiegelt.
      // promo_view meldet die Chat-Seite erst, wenn der Banner dort
      // tatsaechlich gerendert ist. Ein reines Dispatch zaehlt nicht.
      const request: BannerRequest = {
        id: decision.id,
        variant: decision.variant || 'context.adventure.free',
        surface: decision.surface,
        target_tier: decision.target_tier,
        characterName: decision.params.character_name,
      };
      window.dispatchEvent(new CustomEvent<BannerRequest>(PROMO_BANNER_EVENT, { detail: request }));
      promo.consumeDecision(decision.id);
      return;
    }
    promo.consumeDecision(decision.id);
  }, [decision, gate, isPreview, lockOver, modal, promo, routeExcluded]);

  // S6 Funnel: dismiss/click aus dem Dialog heraus melden.
  useEffect(() => {
    if (!modal) return undefined;
    const onDismiss = () => {
      void reportFunnelEvent({
        event: 'promo_dismiss',
        exposure_id: modal.id,
        variant: modal.variant,
        source: 'dialog',
        tier: modal.target_tier || undefined,
      }).catch(() => undefined);
    };
    const onClick = () => {
      void reportFunnelEvent({
        event: 'promo_click',
        exposure_id: modal.id,
        variant: modal.variant,
        source: 'dialog',
        tier: modal.target_tier || undefined,
      }).catch(() => undefined);
    };
    window.addEventListener('lorath:promo-modal-dismiss', onDismiss as EventListener);
    window.addEventListener('lorath:promo-modal-click', onClick as EventListener);
    return () => {
      window.removeEventListener('lorath:promo-modal-dismiss', onDismiss as EventListener);
      window.removeEventListener('lorath:promo-modal-click', onClick as EventListener);
    };
  }, [modal]);

  // Raeumt sich weg, wenn ein Gate nachtraeglich kommt (W8-C-Muster). Ein
  // weggeraeumter Intro-Dialog gilt nicht als gesehen.
  useEffect(() => {
    if (gate === 'none') return;
    if (!modal) return;
    setModal(null);
  }, [gate, modal]);

  if (gate !== 'none') return null;
  if (!modal) return notices;

  const hintKey = segment === 'plus' || segment === 'plus_trial'
    ? 'promo.intro.member.upgrade_hint.plus'
    : segment === 'ultra'
      ? 'promo.intro.member.upgrade_hint.ultra'
      : 'promo.intro.member.upgrade_hint.none';
  const textParams = {
    name: modal.params.character_name ?? '',
    tier: modal.target_tier ? t(`tier.${modal.target_tier}`) : '',
    hint: modal.isIntro ? t(hintKey) : '',
  };
  const dialogTier = modal.target_tier === 'plus' || modal.target_tier === 'ultra' || modal.target_tier === 'founders'
    ? modal.target_tier
    : membership.tier === 'torchbearer' ? 'torchbearer' : 'ultra';

  const close = () => {
    if (modal.isIntro) void promo.ackIntroSeen();
    else void promo.ackAction(modal.id, 'later');
    window.dispatchEvent(new CustomEvent('lorath:promo-modal-dismiss', { detail: { id: modal.id } }));
    setModal(null);
  };
  const open = () => {
    if (modal.isIntro) void promo.ackIntroSeen();
    else void promo.ackAction(modal.id, 'open');
    window.dispatchEvent(new CustomEvent('lorath:promo-modal-click', { detail: { id: modal.id } }));
    // P2-4: variant + exposure_id bleiben auf dem Weg zur Paywall erhalten.
    promo.navigateToMembership(modal.target_tier, modal.variant, modal.id);
    setModal(null);
  };

  return (
    <>{notices}<PromoDialog
      open
      onClose={close}
      title={t(`promo.${modal.variant}.title`, textParams)}
      text={t(`promo.${modal.variant}.text`, textParams)}
      benefits={[1, 2, 3].map((index) => t(`promo.${modal.variant}.benefit${index}`, textParams))}
      ctaLabel={t(`promo.${modal.variant}.cta`)}
      onCta={open}
      tier={dialogTier}
    /></>
  );
}

export { PROMO_PREVIEW_STORAGE_KEY };
